import { ERequestType } from "../../Enum/ERequestType"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { useError } from "../../context/useErrorContext"
import { useLoad } from "../../context/useLoadContext"
import { AdminPlan } from "../../Objects/AdminPlan/AdminPlan"
import { AdminPlanCoupon } from "../../Objects/AdminPlan/AdminPlanCoupon"
import { AdminUpdatePlanPeriod } from "../../Objects/AdminPlan/AdminUpdatePlanPeriod"
import { AdminCreatePlanPeriod } from "../../Objects/AdminPlan/AdminCreatePlanPeriod"
import { AdminSavePlanCoupon } from "../../Objects/AdminPlan/AdminSavePlanCoupon"
import { AdminCreatePlan } from "../../Objects/AdminPlan/AdminCreatePlan"
import { AdminUpdatePlan } from "../../Objects/AdminPlan/AdminUpdatePlan"
import { GetRequest, PostRequest, PutRequest } from "../../services/ApiClientService"

export const useAdminPlan = () => {
  const { enableLoad, disableLoad } = useLoad()

  const { setError } = useError()

  const handleGetAllPlans = async (onSuccess: (data: AdminPlan[]) => void) => {
    enableLoad()

    try {
      const response = await GetRequest<AdminPlan[]>(API_ROUTES.ADMIN_PLAN.GET_ALL, ERequestType.Admin)

      if (!response.success) {
        throw new Error(response.message)
      }

      onSuccess((response.data ?? []).map((item) => new AdminPlan(item)))
    } catch (e) {
      const message = e instanceof Error ? e.message : "Erro ao carregar os planos de assinatura."
      setError("Planos de assinatura", message)
    } finally {
      disableLoad()
    }
  }

  const handleCreatePlan = async (request: AdminCreatePlan, onSuccess: () => void) => {
    enableLoad()

    try {
      const response = await PostRequest(API_ROUTES.ADMIN_PLAN.CREATE, request, ERequestType.Admin)

      if (!response.success) {
        throw new Error(response.message)
      }

      onSuccess()
    } catch (e) {
      const message = e instanceof Error ? e.message : "Erro ao criar o plano de assinatura."
      setError("Planos de assinatura", message)
    } finally {
      disableLoad()
    }
  }

  const handleUpdatePlan = async (planId: number, request: AdminUpdatePlan, onSuccess: () => void) => {
    enableLoad()

    try {
      const response = await PutRequest(API_ROUTES.ADMIN_PLAN.UPDATE(planId), request, ERequestType.Admin)

      if (!response.success) {
        throw new Error(response.message)
      }

      onSuccess()
    } catch (e) {
      const message = e instanceof Error ? e.message : "Erro ao atualizar o plano de assinatura."
      setError("Planos de assinatura", message)
    } finally {
      disableLoad()
    }
  }

  const handleUpdatePlanPeriod = async (planPeriodId: number, request: AdminUpdatePlanPeriod, onSuccess: () => void) => {
    enableLoad()

    try {
      const response = await PutRequest(API_ROUTES.ADMIN_PLAN.UPDATE_PERIOD(planPeriodId), request, ERequestType.Admin)

      if (!response.success) {
        throw new Error(response.message)
      }

      onSuccess()
    } catch (e) {
      const message = e instanceof Error ? e.message : "Erro ao atualizar o período do plano."
      setError("Planos de assinatura", message)
    } finally {
      disableLoad()
    }
  }

  const handleCreatePlanPeriod = async (planId: number, request: AdminCreatePlanPeriod, onSuccess: () => void) => {
    enableLoad()

    try {
      const response = await PostRequest(API_ROUTES.ADMIN_PLAN.CREATE_PERIOD(planId), request, ERequestType.Admin)

      if (!response.success) {
        throw new Error(response.message)
      }

      onSuccess()
    } catch (e) {
      const message = e instanceof Error ? e.message : "Erro ao criar o período do plano."
      setError("Planos de assinatura", message)
    } finally {
      disableLoad()
    }
  }

  const handleGetAllCoupons = async (onSuccess: (data: AdminPlanCoupon[]) => void) => {
    enableLoad()

    try {
      const response = await GetRequest<AdminPlanCoupon[]>(API_ROUTES.ADMIN_PLAN.GET_ALL_COUPONS, ERequestType.Admin)

      if (!response.success) {
        throw new Error(response.message)
      }

      onSuccess((response.data ?? []).map((item) => new AdminPlanCoupon(item)))
    } catch (e) {
      const message = e instanceof Error ? e.message : "Erro ao carregar os cupons de assinatura."
      setError("Cupons de assinatura", message)
    } finally {
      disableLoad()
    }
  }

  const handleCreateCoupon = async (request: AdminSavePlanCoupon, onSuccess: () => void) => {
    enableLoad()

    try {
      const response = await PostRequest(API_ROUTES.ADMIN_PLAN.CREATE_COUPON, request, ERequestType.Admin)

      if (!response.success) {
        throw new Error(response.message)
      }

      onSuccess()
    } catch (e) {
      const message = e instanceof Error ? e.message : "Erro ao criar o cupom de assinatura."
      setError("Cupons de assinatura", message)
    } finally {
      disableLoad()
    }
  }

  const handleUpdateCoupon = async (couponId: number, request: AdminSavePlanCoupon, onSuccess: () => void) => {
    enableLoad()

    try {
      const response = await PutRequest(API_ROUTES.ADMIN_PLAN.UPDATE_COUPON(couponId), request, ERequestType.Admin)

      if (!response.success) {
        throw new Error(response.message)
      }

      onSuccess()
    } catch (e) {
      const message = e instanceof Error ? e.message : "Erro ao atualizar o cupom de assinatura."
      setError("Cupons de assinatura", message)
    } finally {
      disableLoad()
    }
  }

  const handleDeactivateCoupon = async (couponId: number, onSuccess: () => void) => {
    enableLoad()

    try {
      const response = await PostRequest(API_ROUTES.ADMIN_PLAN.DEACTIVATE_COUPON(couponId), {}, ERequestType.Admin)

      if (!response.success) {
        throw new Error(response.message)
      }

      onSuccess()
    } catch (e) {
      const message = e instanceof Error ? e.message : "Erro ao desativar o cupom de assinatura."
      setError("Cupons de assinatura", message)
    } finally {
      disableLoad()
    }
  }

  const handleApproveCoupon = async (couponId: number, onSuccess: () => void) => {
    enableLoad()

    try {
      const response = await PostRequest(API_ROUTES.ADMIN_PLAN.APPROVE_COUPON(couponId), {}, ERequestType.Admin)

      if (!response.success) {
        throw new Error(response.message)
      }

      onSuccess()
    } catch (e) {
      const message = e instanceof Error ? e.message : "Erro ao aprovar o cupom solicitado."
      setError("Cupons de assinatura", message)
    } finally {
      disableLoad()
    }
  }

  const handleRejectCoupon = async (couponId: number, reason: string | undefined, onSuccess: () => void) => {
    enableLoad()

    try {
      const response = await PostRequest(API_ROUTES.ADMIN_PLAN.REJECT_COUPON(couponId), { reason }, ERequestType.Admin)

      if (!response.success) {
        throw new Error(response.message)
      }

      onSuccess()
    } catch (e) {
      const message = e instanceof Error ? e.message : "Erro ao rejeitar o cupom solicitado."
      setError("Cupons de assinatura", message)
    } finally {
      disableLoad()
    }
  }

  return {
    handleGetAllPlans,
    handleCreatePlan,
    handleUpdatePlan,
    handleUpdatePlanPeriod,
    handleCreatePlanPeriod,
    handleGetAllCoupons,
    handleCreateCoupon,
    handleUpdateCoupon,
    handleDeactivateCoupon,
    handleApproveCoupon,
    handleRejectCoupon,
  }
}
